import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  calculateUnrealizedPnLPercent,
  isPositionShort,
  renderLiquidationPrice,
} from 'api/apiUtils';
import { useOrdersQuery } from 'api/queries/orderQueries';
import { usePositionQuery } from 'api/queries/walletPositionsQueries';
import Button from 'components/basic/Button/Button';
import RouterLink from 'components/basic/Link/RouterLink';
import SimpleModalOrMobilePage from 'components/basic/modal/SimpleModalOrMobilePage';
import DataPair from 'components/basic/panels/DataPair';
import TextColored from 'components/basic/text/TextColored';
import { useFetchInitialMarginFractionOverride } from 'components/trading/BuySellPanel/leverage/leverageUtils';
import { MobileDataPairCoinIcon } from 'config/coinIcons/CoinIcon';
import { TPSL_ENABLED } from 'config/constants';
import {
  formatNumber,
  formatPercentage,
  formatUsdWithSign,
} from 'lib/display/formatting';
import {
  getDashboardUrl,
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from 'reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from 'reduxState/storeCore';

import ADLIndicator from '../ADLIndicator';
import { useClosePosition } from '../closePositions';
import TPSLColumnWrap from '../takeProfitStopLoss/TPSLColumnWrap';
import TPSLModal from '../takeProfitStopLoss/TPSLModal';

const FieldsWrap = styled.div`
  padding: 20px 0;
`;

export default function PositionDetailPageMobile({
  market,
  onClose,
  vaultManagerWallet,
}: {
  market: string;
  onClose: () => void;
  vaultManagerWallet?: string;
}) {
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  const [baseSymbol] = useMemo(() => market?.split('-') || [], [market]);

  const { data: position, isLoading } = usePositionQuery({
    market,
    vaultManagerWallet,
  });

  const marketsLeverageParams = useSelector(
    state => state.markets.listedMarkets.marketsLeverageParams
  );
  const initialMarginFractionOverride = useFetchInitialMarginFractionOverride({
    isVault: !!vaultManagerWallet,
    market,
    walletAddress: vaultManagerWallet ?? walletAddress,
  });
  const tickSizeDecimals = useSelectMarketTickSize(market)?.visibleDecimals;
  const stepSizeDecimals = useSelectMarketStepSize(market)?.visibleDecimals;

  const { closeModalPlaceholder, showClosePositionModal } =
    useClosePosition(onClose);

  const { data: allOrders } = useOrdersQuery();
  const [editTpSl, setEditTpSl] = useState(false);

  if (editTpSl && position?.market) {
    return (
      <TPSLModal onClose={() => setEditTpSl(false)} market={position.market} />
    );
  }

  if (isLoading || !position) {
    return (
      <SimpleModalOrMobilePage
        title="Position Details"
        onClose={onClose}
        size="small"
        verticalAlignOnMobile="ceneteredDiv"
      >
        <h2 style={{ textAlign: 'center' }}>
          {isLoading ? 'Loading...' : `Position ${market} not found`}
        </h2>
      </SimpleModalOrMobilePage>
    );
  }

  const {
    quantity,
    value,
    entryPrice,
    indexPrice,
    liquidationPrice,
    marginRequirement,
    unrealizedPnL,
    realizedPnL,
    adlQuintile,
  } = position;

  const isShort = isPositionShort({ quantity: quantity || '0' });
  const unrealizedPnLNum = Number(unrealizedPnL);
  const realizedPnLNum = Number(realizedPnL);
  const unrealizedPnLPercent =
    initialMarginFractionOverride !== undefined
      ? calculateUnrealizedPnLPercent(
          position,
          marketsLeverageParams?.[position.market],
          initialMarginFractionOverride
        )
      : null;

  return closeModalPlaceholder ? (
    closeModalPlaceholder
  ) : (
    <SimpleModalOrMobilePage
      title="Position Details"
      onClose={onClose}
      size="small"
      verticalAlignOnMobile="ceneteredDiv"
    >
      <FieldsWrap>
        {/* <MobileDataPair label="Date">
              {formatDateTime(position.time, { seconds: true })}
            </MobileDataPair> */}

        <DataPair label="Market">
          <RouterLink to={getDashboardUrl('katanaEVMChain', market)}>
            {baseSymbol && <MobileDataPairCoinIcon symbol={baseSymbol} />}
            {market}
          </RouterLink>
        </DataPair>

        <DataPair label="Quantity" altBackground>
          <TextColored color={isShort ? 'red' : 'green'}>
            {formatNumber(quantity, { fixedDecimals: stepSizeDecimals })}{' '}
            {baseSymbol}
          </TextColored>
        </DataPair>

        <DataPair label="Value">{formatUsdWithSign(value)}</DataPair>

        <DataPair label="Entry Price" altBackground>
          {formatNumber(entryPrice, { fixedDecimals: tickSizeDecimals })}
        </DataPair>

        <DataPair label="Index Price" altBackground>
          {formatNumber(indexPrice, { fixedDecimals: tickSizeDecimals })}
        </DataPair>

        <DataPair label="Liquidation Price">
          {renderLiquidationPrice(liquidationPrice, tickSizeDecimals)}
        </DataPair>

        <DataPair label="Position Margin" altBackground>
          {formatUsdWithSign(marginRequirement)}
        </DataPair>

        <DataPair label="Unrealized P&L (%)">
          <TextColored
            color={
              unrealizedPnLNum === 0
                ? 'default'
                : unrealizedPnLNum < 0
                ? 'red'
                : 'green'
            }
          >
            {formatUsdWithSign(unrealizedPnLNum)}{' '}
            {unrealizedPnLPercent && (
              <>({formatPercentage(unrealizedPnLPercent)}%)</>
            )}
          </TextColored>
        </DataPair>

        <DataPair label="Realized P&L" altBackground>
          <TextColored color={realizedPnLNum < 0 ? 'red' : 'green'}>
            {formatUsdWithSign(realizedPnLNum)}
          </TextColored>
        </DataPair>

        {TPSL_ENABLED && !vaultManagerWallet && (
          <DataPair label="TP/SL">
            <TPSLColumnWrap
              allOrders={allOrders}
              market={market}
              onEdit={() => setEditTpSl(true)}
              tickSizeDecimals={tickSizeDecimals}
            />
          </DataPair>
        )}

        <DataPair
          label="ADL"
          altBackground={TPSL_ENABLED && !vaultManagerWallet}
        >
          <ADLIndicator currentLiquidationChance={adlQuintile} />
        </DataPair>
      </FieldsWrap>

      {/* showClosePositionModal */}
      {!vaultManagerWallet && (
        <Button
          bordered
          color="red"
          onClick={() => {
            showClosePositionModal(position.market);
          }}
        >
          Close Position
        </Button>
      )}
      {/* <Button
            bordered
            color="red"
            onClick={() => {
              showCloseByLimitModal(position.market);
            }}
          >
            Limit Close Position
          </Button>
          <div style={{ height: 8 }} />
          <Button
            bordered
            color="red"
            onClick={() => {
              showCloseByMarketModal(position.market);
            }}
          >
            Market Close Position
          </Button> */}
    </SimpleModalOrMobilePage>
  );
}
